import { buildMarginal, resolveMonthlyFeePct } from './xirrFee';
import { applyRepaymentCapacity } from './cohortRepayments';
import {
  resolveCohortDraw,
  resolveEffectiveRevSharePct,
} from './resolvePlanFunding';

export function buildCohortRepaymentSpecs(
  state,
  numCohorts,
  cohortRoasVectors,
  gracePeriod
) {
  const grace = Math.max(0, Number(gracePeriod) || 0);

  return Array.from({ length: numCohorts }, (_, i) => {
    const drawMonth = i + 1;
    const drawAmount = resolveCohortDraw(state, i);
    const marketing = Number(state?.marketing?.[i]) || 0;
    const actualFundingPct =
      marketing > 0 ? (drawAmount / marketing) * 100 : 0;
    const roas = [...(cohortRoasVectors?.[i] ?? state.roas ?? [])];
    const maxRepaymentPeriodCount = roas.length;
    const finalMonth = drawMonth + grace + maxRepaymentPeriodCount - 1;

    return {
      drawMonth,
      drawAmount,
      marketing: state.marketing[i],
      revSharePct: resolveEffectiveRevSharePct(state, i, actualFundingPct),
      marginal: buildMarginal(roas),
      maxRepaymentPeriodCount,
      finalMonth,
      cumulativeRoas: roas,
    };
  });
}

export function estimateMaxRepaymentMonth(cohortSpecs) {
  if (!cohortSpecs.length) {
    return 0;
  }
  return Math.max(...cohortSpecs.map((spec) => spec.finalMonth));
}

function splitFeeAccrual(totalFeeAccrual, cohortWeights) {
  const weightSum = cohortWeights.reduce((sum, weight) => sum + weight, 0);
  if (totalFeeAccrual <= 0) {
    return cohortWeights.map(() => 0);
  }
  if (weightSum <= 0) {
    const shares = cohortWeights.map(() => 0);
    if (cohortWeights.length > 0) {
      shares[0] = totalFeeAccrual;
    }
    return shares;
  }

  const cohortShares = cohortWeights.map((weight) =>
    weight > 0 ? Math.round((totalFeeAccrual * weight) / weightSum) : 0
  );
  const allocated = cohortShares.reduce((sum, share) => sum + share, 0);
  const remainder = totalFeeAccrual - allocated;
  if (remainder !== 0 && cohortShares.length > 0) {
    const targetIndex = cohortWeights.findIndex((weight) => weight > 0);
    cohortShares[targetIndex >= 0 ? targetIndex : 0] += remainder;
  }
  return cohortShares;
}

function computeCohortCapacity(spec, month, gracePeriod) {
  if (month < spec.drawMonth + gracePeriod) {
    return 0;
  }
  const k = month - spec.drawMonth - gracePeriod;
  if (k < 0 || k >= spec.maxRepaymentPeriodCount) {
    return 0;
  }
  const cohortRevenue = (spec.marketing * (spec.marginal[k] ?? 0)) / 100;
  return Math.round((cohortRevenue * spec.revSharePct) / 100);
}

function isAllOutstandingZero(
  cohortOutstandingPrincipal,
  cohortOutstandingFee,
  outstandingFacilityPrincipal
) {
  if (outstandingFacilityPrincipal > 0) {
    return false;
  }
  for (let i = 0; i < cohortOutstandingPrincipal.length; i += 1) {
    if (cohortOutstandingPrincipal[i] > 0 || cohortOutstandingFee[i] > 0) {
      return false;
    }
  }
  return true;
}

function resolveBalloonSettleMonth(repay, lastMonth) {
  for (let month = lastMonth; month >= 0; month -= 1) {
    if (repay[month] !== 0) {
      return month;
    }
  }
  return lastMonth;
}

function zeroResidualEndBal(month, state) {
  const residual = state.endBal[month];
  if (residual === 0) {
    return false;
  }
  state.repay[month] -= residual;
  state.endBal[month] = 0;
  if (state.numCohorts > 0) {
    state.cohortRepayments[state.numCohorts - 1][month] += residual;
  }
  return true;
}

function recalcBalanceChainFromMonth(startMonth, state) {
  for (let month = Math.max(1, startMonth); month < state.N_OUT; month += 1) {
    state.beginBal[month] = state.endBal[month - 1];
    state.endBal[month] =
      state.beginBal[month] +
      state.draw[month] +
      state.xirrFee[month] +
      state.facFeeRow[month] +
      state.repay[month];
  }
}

/** Once repayments finish, trailing ending balances must be exactly zero. */
function settleTailResidualEndingBalances(state) {
  let lastRepayMonth = -1;
  for (let month = 0; month < state.N_OUT; month += 1) {
    if (state.repay[month] !== 0) {
      lastRepayMonth = month;
    }
  }
  if (lastRepayMonth < 0) {
    return;
  }

  const maxMicroEndBal = Math.max(100, state.numCohorts);
  let startMonth = lastRepayMonth;
  while (
    startMonth > 0 &&
    state.endBal[startMonth - 1] !== 0 &&
    Math.abs(state.endBal[startMonth - 1]) <= maxMicroEndBal
  ) {
    startMonth -= 1;
  }

  for (let pass = 0; pass < state.N_OUT; pass += 1) {
    let changed = false;
    for (let month = startMonth; month < state.N_OUT; month += 1) {
      if (state.endBal[month] !== 0) {
        zeroResidualEndBal(month, state);
        recalcBalanceChainFromMonth(month + 1, state);
        changed = true;
      }
    }
    if (!changed) {
      break;
    }
  }
}

/**
 * Month-by-month Twelve balance simulation.
 * Fee accrues on cohort portion of beginning balance (excludes unpaid facility fee).
 * Cohort rev share pays own principal then own fees; facility fee is repaid externally on schedule.
 */
export function simulateTwelveBalance({
  numCohorts,
  N_OUT,
  draw,
  facFeeRow,
  facilityFeeContext,
  monthlyFeePct,
  gracePeriod,
  cohortSpecs,
}) {
  const beginBal = new Array(N_OUT).fill(0);
  const endBal = new Array(N_OUT).fill(0);
  const xirrFee = new Array(N_OUT).fill(0);
  const repay = new Array(N_OUT).fill(0);
  const cohortRepayments = Array.from({ length: numCohorts }, () =>
    new Array(N_OUT).fill(0)
  );
  const cohortFeeRepayments = Array.from({ length: numCohorts }, () =>
    new Array(N_OUT).fill(0)
  );
  const cohortPrincipalRepayments = Array.from({ length: numCohorts }, () =>
    new Array(N_OUT).fill(0)
  );
  const cohortFeeAccrualByMonth = Array.from({ length: numCohorts }, () => ({}));

  const cohortOutstandingPrincipal = cohortSpecs.map(() => 0);
  const cohortOutstandingFee = cohortSpecs.map(() => 0);
  let outstandingFacilityPrincipal = 0;

  const {
    facilityFeePaymentSchedule = {},
    facilityFeeLastPaymentMonth = -1,
  } = facilityFeeContext;

  let endBalPrev = 0;

  for (let month = 0; month < N_OUT; month += 1) {
    const beginBalMonth = month === 0 ? 0 : endBalPrev;
    beginBal[month] = beginBalMonth;

    let feeAccrual = 0;
    if (month > 0 && monthlyFeePct > 0) {
      const cohortBase = Math.max(
        0,
        beginBalMonth - outstandingFacilityPrincipal
      );
      if (cohortBase > 0) {
        feeAccrual = Math.round((cohortBase * monthlyFeePct) / 100);
      }
    }
    xirrFee[month] = feeAccrual;

    if (feeAccrual > 0) {
      const cohortWeights = cohortSpecs.map(
        (_, i) => cohortOutstandingPrincipal[i] + cohortOutstandingFee[i]
      );
      const cohortShares = splitFeeAccrual(feeAccrual, cohortWeights);

      cohortShares.forEach((share, i) => {
        if (share > 0) {
          cohortOutstandingFee[i] += share;
          cohortFeeAccrualByMonth[i][month] = share;
        }
      });
    }

    cohortSpecs.forEach((spec, i) => {
      if (spec.drawMonth === month && spec.drawAmount > 0) {
        cohortOutstandingPrincipal[i] += spec.drawAmount;
      }
    });

    if (facFeeRow[month] > 0) {
      outstandingFacilityPrincipal += facFeeRow[month];
    }

    let repayMonth = 0;

    cohortSpecs.forEach((spec, i) => {
      if (month < spec.drawMonth || spec.drawAmount <= 0) {
        return;
      }

      const capacity = computeCohortCapacity(spec, month, gracePeriod);
      const repaymentsByMonth = {};
      const principalRepaymentsByMonth = {};
      const feeRepaymentsByMonth = {};

      const { outstandingPrincipal, outstandingFee } = applyRepaymentCapacity({
        month,
        capacity,
        outstandingPrincipal: cohortOutstandingPrincipal[i],
        outstandingFee: cohortOutstandingFee[i],
        repaymentsByMonth,
        principalRepaymentsByMonth,
        feeRepaymentsByMonth,
      });

      cohortOutstandingPrincipal[i] = outstandingPrincipal;
      cohortOutstandingFee[i] = outstandingFee;

      const principalPay = principalRepaymentsByMonth[month] || 0;
      const feePay = feeRepaymentsByMonth[month] || 0;
      const totalPay = principalPay + feePay;

      if (month === spec.finalMonth) {
        const balloonPrincipal = cohortOutstandingPrincipal[i];
        const balloonFee = cohortOutstandingFee[i];
        if (balloonPrincipal > 0 || balloonFee > 0) {
          cohortOutstandingPrincipal[i] = 0;
          cohortOutstandingFee[i] = 0;
          cohortRepayments[i][month] = totalPay + balloonPrincipal + balloonFee;
          cohortPrincipalRepayments[i][month] = principalPay + balloonPrincipal;
          cohortFeeRepayments[i][month] = feePay + balloonFee;
          repayMonth -= totalPay + balloonPrincipal + balloonFee;
          return;
        }
      }

      if (totalPay > 0) {
        cohortRepayments[i][month] = totalPay;
        cohortPrincipalRepayments[i][month] = principalPay;
        cohortFeeRepayments[i][month] = feePay;
        repayMonth -= totalPay;
      }
    });

    const scheduledFacilityPay = facilityFeePaymentSchedule[month];
    if (
      scheduledFacilityPay != null &&
      scheduledFacilityPay > 0 &&
      outstandingFacilityPrincipal > 0
    ) {
      let externalPay = Math.min(
        scheduledFacilityPay,
        outstandingFacilityPrincipal
      );
      if (month === facilityFeeLastPaymentMonth) {
        externalPay = outstandingFacilityPrincipal;
      }
      repayMonth -= externalPay;
      outstandingFacilityPrincipal -= externalPay;
    }

    repay[month] = repayMonth;
    endBal[month] =
      beginBalMonth + draw[month] + feeAccrual + facFeeRow[month] + repayMonth;

    if (
      isAllOutstandingZero(
        cohortOutstandingPrincipal,
        cohortOutstandingFee,
        outstandingFacilityPrincipal
      ) &&
      endBal[month] !== 0
    ) {
      const roundingTrueUp = endBal[month];
      repay[month] -= roundingTrueUp;
      endBal[month] = 0;
      if (numCohorts > 0) {
        cohortRepayments[numCohorts - 1][month] += roundingTrueUp;
      }
    }

    endBalPrev = endBal[month];
  }

  const lastMonth = N_OUT - 1;
  if (lastMonth >= 0) {
    let balloonRepay = 0;
    cohortSpecs.forEach((_, i) => {
      balloonRepay += cohortOutstandingPrincipal[i] + cohortOutstandingFee[i];
      cohortOutstandingPrincipal[i] = 0;
      cohortOutstandingFee[i] = 0;
    });
    balloonRepay += outstandingFacilityPrincipal;
    outstandingFacilityPrincipal = 0;

    if (endBal[lastMonth] > 0) {
      balloonRepay = Math.max(balloonRepay, endBal[lastMonth]);
    }

    if (balloonRepay > 0) {
      const settleMonth = resolveBalloonSettleMonth(repay, lastMonth);
      repay[settleMonth] -= balloonRepay;
      endBal[settleMonth] -= balloonRepay;
      if (numCohorts > 0) {
        cohortRepayments[numCohorts - 1][settleMonth] += balloonRepay;
      }
      for (let month = settleMonth + 1; month <= lastMonth; month += 1) {
        beginBal[month] = 0;
        if (draw[month] === 0 && facFeeRow[month] === 0 && xirrFee[month] === 0) {
          repay[month] = 0;
          endBal[month] = 0;
        }
      }
    }
  }

  settleTailResidualEndingBalances({
    beginBal,
    endBal,
    draw,
    xirrFee,
    facFeeRow,
    repay,
    cohortRepayments,
    numCohorts,
    N_OUT,
  });

  return {
    beginBal,
    endBal,
    xirrFee,
    repay,
    cohortRepayments,
    cohortFeeRepayments,
    cohortPrincipalRepayments,
    cohortFeeAccrualByMonth,
  };
}

export function resolveMonthlyFeePctFromState(state) {
  return resolveMonthlyFeePct(state.feeType, state.feePct);
}
